<script setup lang="ts">
import {
  Image as DatocmsImage,
  StructuredText as DatocmsStructuredText,
} from 'vue-datocms'

import DatoSectionContainer from '~/components/dato/DatoSectionContainer.vue'

import { useGetMediaUrl } from '~/composables/useGetMediaUrl'

import { isNonNullAndDefined } from '~/utils/type-guards'

import type { TestimonialShortFragment } from '~/graphql/generated'

type Props = TestimonialShortFragment

const props = defineProps<Props>()
const { getMediaUrl } = useGetMediaUrl()

// Reactive state for animation trigger
const isVisible = ref(false)

// Using Nuxt's built-in intersection observer
const testimonialRef = ref<HTMLElement>()
const { stop } = useIntersectionObserver(
  testimonialRef,
  ([{ isIntersecting }]) => {
    if (isIntersecting && !isVisible.value) {
      isVisible.value = true
      // Once animated, we can stop observing
      stop()
    }
  },
  {
    threshold: 0.2,
    rootMargin: '0px 0px -50px 0px',
  },
)
</script>

<template>
  <DatoSectionContainer>
    <div class="tw-my-5xl tw-w-full">
      <div
        ref="testimonialRef"
        class="testimonial-card tw-relative tw-w-full tw-transform tw-rounded-2xl tw-bg-fill-alt tw-px-2xl tw-py-2xl tw-transition-transform tw-duration-[600ms] tw-ease-[cubic-bezier(0.25,0.46,0.45,0.94)] md:tw-px-8xl"
        :class="[
          isVisible
            ? 'tw-translate-y-0 tw-opacity-100'
            : 'tw-translate-y-[30px] tw-opacity-0',
        ]"
      >
        <!-- Decorative quote marks with staggered animation -->
        <img
          :src="getMediaUrl('quote-start.svg')"
          loading="lazy"
          class="quote-mark tw-absolute tw-left-md tw-top-4 tw-w-[56px] tw-transform tw-transition-all tw-duration-[500ms] tw-ease-[cubic-bezier(0.34,1.56,0.64,1)] md:tw-top-12 md:tw-w-[80px]"
          :class="[
            isVisible
              ? 'tw-translate-y-[-100%] tw-rotate-0 tw-scale-100 tw-opacity-100 tw-delay-[200ms]'
              : 'tw-scale-70 tw-translate-y-[-100%] tw-rotate-[-5deg] tw-opacity-0',
          ]"
          alt="Opening quote"
        />
        <img
          :src="getMediaUrl('quote-end.svg')"
          loading="lazy"
          class="quote-mark tw-absolute tw-bottom-4 tw-right-md tw-w-[56px] tw-transform tw-transition-all tw-duration-[500ms] tw-ease-[cubic-bezier(0.34,1.56,0.64,1)] md:tw-bottom-12 md:tw-w-[80px]"
          :class="[
            isVisible
              ? 'tw-translate-y-[100%] tw-rotate-0 tw-scale-100 tw-opacity-100 tw-delay-[400ms]'
              : 'tw-scale-70 tw-translate-y-[100%] tw-rotate-[-5deg] tw-opacity-0',
          ]"
          alt="Closing quote"
        />

        <!-- Quote text with delayed animation -->
        <h2
          class="quote-text tw-text-h5 tw-mb-xl tw-transform tw-text-center tw-text-inverse tw-transition-all tw-duration-[600ms] tw-ease-[cubic-bezier(0.25,0.46,0.45,0.94)]"
          :class="[
            isVisible
              ? 'tw-translate-y-0 tw-opacity-100 tw-delay-[300ms]'
              : 'tw-translate-y-[20px] tw-opacity-0',
          ]"
        >
          <DatocmsStructuredText v-if="quote" :data="quote" />
        </h2>

        <!-- Person info with final delayed animation -->
        <div
          class="person-info tw-flex tw-transform tw-items-center tw-justify-center tw-gap-x-md tw-transition-all tw-duration-[500ms] tw-ease-[cubic-bezier(0.25,0.46,0.45,0.94)]"
          :class="[
            isVisible
              ? 'tw-translate-y-0 tw-opacity-100 tw-delay-[500ms]'
              : 'tw-translate-y-[15px] tw-opacity-0',
          ]"
        >
          <DatocmsImage
            v-if="isNonNullAndDefined(avatarImage?.responsiveImage)"
            :data="avatarImage.responsiveImage"
            class="tw-size-4xl tw-rounded-full tw-object-cover"
          />
          <div
            class="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-gap-x-xs"
          >
            <div class="tw-text-body-base">
              <span class="tw-font-bold tw-text-inverse-accent">{{
                nameOfThePerson
              }}</span
              ><span v-if="secondaryText" class="tw-text-inverse"
                >, {{ secondaryText }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </DatoSectionContainer>
</template>

<style scoped lang="scss">
// Respect users who prefer reduced motion
@media (prefers-reduced-motion: reduce) {
  .testimonial-card,
  .quote-mark,
  .quote-text,
  .person-info {
    @apply tw-transform-none tw-opacity-100 tw-transition-none;
  }
}

.quotes {
  &:deep(p:first-of-type)::before {
    content: open-quote;
  }

  &:deep(p:last-of-type)::after {
    content: close-quote;
  }
}
</style>
